<template>
  <section id="clients" class="clients section">
    <div class="container">
      <div class="section-header">
        <h2 class="section-title animate-slide-up">
          {{ $t('clients.title') }}
        </h2>
        <p class="section-subtitle animate-slide-up">
          {{ $t('clients.subtitle') }}
        </p>
      </div>
      
      <div class="clients-logos-grid">
        <div 
          v-for="(client, index) in clients" 
          :key="index" 
          class="client-logo-item animate-slide-up"
          :class="{ 'clickable': client.website }"
          :style="{ animationDelay: `${index * 0.1}s` }"
          @click="openClientWebsite(client.website)"
        >
          <img
            :src="client.logo"
            :alt="`Logo ${client.name}`"
            class="client-logo-img"
            :class="{ 'oscar-escher-logo': client.name === 'OE Arquitetos e Urbanistas SS' }"
            :title="client.name"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'

// Dados dos clientes reais
const clients = ref([
  {
    name: 'Industrial Boituva de Bebidas S.A.',
    sector: '',
    logo: '/images/clientes/boituva.png',
    website: 'https://www.industrialboituva.com.br/'
  },
  {
    name: 'OE Arquitetos e Urbanistas SS',
    sector: '',
    logo: '/images/clientes/OscarEscher.png',
    website: 'https://escher.arq.br'
  },
  {
    name: 'Rede de Postos Metropolitano',
    sector: '',
    logo: '/images/clientes/postometropolitano.png',
    website: 'https://www.postosmetropolitano.com.br'
  },
  {
    name: 'SINPROCAN',
    sector: 'Sindicato',
    logo: '/images/clientes/sinprocan.jpg',
    website: 'https://sinprocan.org.br'
  },
  {
    name: 'Aqualeve',
    sector: '',
    logo: '/images/clientes/aqualeve.png'
  },
  {
    name: 'EAEC',
    sector: '',
    logo: '/images/clientes/eaec.jpeg'
  }
])

// Função para abrir site do cliente
const openClientWebsite = (website?: string) => {
  if (website) {
    window.open(website, '_blank', 'noopener,noreferrer')
  }
}
</script>

<style scoped>
.clients {
  background: var(--background-primary);
  position: relative;
  padding: var(--space-20) 0;
}

.clients::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--border-medium), transparent);
}

.clients-logos-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: var(--space-12);
  margin-bottom: var(--space-16);
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  padding: var(--space-8) 0;
}

.client-logo-item {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  padding: var(--space-4) var(--space-6);
  background: var(--background-card);
  border-radius: var(--border-radius-md);
  border: 1px solid var(--border-light);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  box-shadow: var(--shadow-xs);
  flex: 0 0 auto;
  min-width: 200px;
}


.client-logo-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--gradient-primary);
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: var(--border-radius-md);
}

.client-logo-item.clickable {
  cursor: pointer;
}

.client-logo-item:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-lg);
  border-color: var(--primary-200);
}

.client-logo-item:hover::before {
  opacity: 0.03;
}

.client-logo-img {
  height: 60px;
  width: auto;
  max-width: 180px;
  object-fit: contain;
  object-position: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 2;
  filter: grayscale(100%) opacity(0.7);
}

.client-logo-img.oscar-escher-logo {
  height: 150px;
  max-width: 400px;
}

.client-logo-item:hover .client-logo-img {
  filter: grayscale(0%) opacity(1);
  transform: scale(1.02);
}


@media (max-width: 768px) {
  .clients-logos-grid {
    gap: var(--space-6);
    padding: var(--space-6) 0;
  }
  
  .client-logo-item {
    height: 80px;
    padding: var(--space-3) var(--space-4);
    min-width: 150px;
  }
  
  .client-logo-img {
    height: 50px;
    max-width: 140px;
  }
  
  .client-logo-img.oscar-escher-logo {
    height: 120px;
    max-width: 300px;
  }
}

@media (max-width: 480px) {
  .clients-logos-grid {
    gap: var(--space-4);
    padding: var(--space-4) 0;
  }
  
  .client-logo-item {
    height: 70px;
    padding: var(--space-2) var(--space-3);
    min-width: 120px;
  }
  
  .client-logo-img {
    height: 40px;
    max-width: 100px;
  }
  
  .client-logo-img.oscar-escher-logo {
    height: 100px;
    max-width: 250px;
  }
}

</style>
